import { useApi } from "@shopexperts/shared";
import { useQuery } from "@tanstack/vue-query";
import { computed, type Ref, toValue } from "vue";
import type { ComboboxItemData, PartnerBadge } from "@shopexperts/shared";

export function badgeToComboboxItem(badge: PartnerBadge): ComboboxItemData {
  return {
    value: badge.id.toString(),
    label: badge.name,
    image: badge.icon?.cdn_url || badge.icon?.url || undefined,
    showImage: true,
    metadata: {
      id: badge.id,
      name: badge.name,
      slug: badge.slug,
      status: badge.status,
      icon: badge.icon,
    },
  };
}

interface UseBadgeSearchOptions {
  search?: Ref<string> | string;
  status?: 'active' | 'inactive';
  limit?: number;
}

export function useBadgeSearch(options: UseBadgeSearchOptions = {}) {
  const api = useApi();
  
  const { search = '', status = 'active', limit = 20 } = options;
  
  // Convert search to computed value that works with both refs and strings
  const searchValue = computed(() => toValue(search));

  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: computed(() => ['admin', 'badges', 'search', { search: searchValue.value, status }]),
    queryFn: async () => {
      return await api.admin.badges.listBadges({
        query: {
          limit,
          search: searchValue.value || undefined,
          status,
        },
      });
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const badges = computed(() => {
    return data.value?.data || [];
  });

  const hasResults = computed(() => badges.value.length > 0);

  // Transform badges to ComboboxItemData format for UiAsyncCombobox
  const comboboxItems = computed<ComboboxItemData[]>(() => {
    return badges.value.map(badgeToComboboxItem);
  });

  return {
    badges,
    comboboxItems,
    isLoading,
    error,
    hasResults,
  };
}

export default useBadgeSearch;
